import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import '@vitesse/base-ui/elements/vitesse-dialog';
import '@vitesse/base-ui/elements/vitesse-button';
import '@vitesse/base-ui/elements/inputs/vitesse-text-input';
import './vitesse-dashboard-background-field';
import { t, LocaleController } from '@vitesse/base-ui/i18n';
import type { DashboardManager } from '../managers/dashboard-manager';
import type { Dashboard, DashboardBackground } from '../../core/domain/dashboard';

/**
 * <vitesse-edit-dashboard-dialog> — what a board IS, as opposed to what is on it.
 *
 * Title, description and the picture behind the tiles. Everything else about a dashboard is edited
 * by touching the board itself — tiles are dragged, resized and configured in place — so this is
 * deliberately the short list of things that have nowhere else to live.
 *
 * A WHOLE-DOCUMENT SAVE, because that is the only kind the gateway accepts. It takes the entire
 * board and refuses a save whose `baseDigest` has moved, rather than merging — last-write-wins on a
 * board a colleague is also editing loses their work without telling anyone. So this reads the body
 * it was given, changes three fields, and hands the whole thing back.
 *
 * THE PICKER IS <vitesse-dashboard-background-field>, shared with the create dialog so one field
 * has one implementation. The detail page carried a comment saying there deliberately was not one:
 * a background arrived through the assistant, which already re-encoded it and already recorded where
 * it came from, and a second entry point risked a third way to set one field — with its own file
 * dialog and its own chance to skip the compression. Two of those three objections are answered by
 * construction here: this calls the SAME `encodeBackground` the assistant's path calls, so there is
 * still exactly one compression, and it leaves `model` empty, which is already how the document
 * spells "a person supplied this" as against "a model invented it". What is left is the judgement
 * that a person should be able to put a picture behind their own board without describing it to a
 * model first, and that judgement went the other way.
 */
@customElement('vitesse-edit-dashboard-dialog')
export class VitesseEditDashboardDialog extends LitElement {
  @property({ type: Boolean }) open = false;
  @property({ attribute: false }) dashboards?: DashboardManager;
  /** The board as it stands. Its digest is what the save is pinned to. */
  @property({ attribute: false }) board?: Dashboard;

  @state() private boardTitle = '';
  @state() private description = '';
  @state() private background?: DashboardBackground;
  @state() private busy = false;
  @state() private error = '';
  /** The board this form was filled from, so reopening on a different board refills it. */
  private filledFrom = '';

  constructor() {
    super();
    new LocaleController(this); // re-render on language change
  }


  static styles = css`
    .field { display: flex; flex-direction: column; gap: 6px; margin-bottom: 14px; }
    label { font-size: 12px; font-weight: 600; color: var(--text-secondary); }
    .error { color: var(--red); font-size: 13px; margin: 0 0 4px; }
  `;

  /* The form tracks the board until the person starts editing it: opening the dialog on a board
   * shows that board, and a background saved by the assistant while it is open does not overwrite
   * what is being typed. */
  willUpdate(): void {
    const id = this.board?.id ?? '';
    if (!this.open || id === this.filledFrom) return;
    this.filledFrom = id;
    this.boardTitle = this.board?.title ?? '';
    this.description = this.board?.description ?? '';
    this.background = this.board?.background;
    this.error = '';
  }

  private close = () => {
    this.filledFrom = ''; // so the next open refills from whatever the board says then
    this.dispatchEvent(new CustomEvent('dialog-close', { bubbles: true, composed: true }));
  };


  private async submit(): Promise<void> {
    const title = this.boardTitle.trim();
    if (!title) {
      this.error = t('dashboards.titleNeeded');
      return;
    }
    if (!this.dashboards || !this.board || this.busy) return;
    this.busy = true;
    this.error = '';
    try {
      const next: Dashboard = { ...this.board, title, description: this.description.trim(), background: this.background };
      const r = await this.dashboards.save(next, this.board.digest);
      if (!r.ok) {
        this.error = r.error || t('dashboards.saveFailed');
        return;
      }
      this.close();
    } catch (e) {
      this.error = e instanceof Error ? e.message : String(e);
    } finally {
      this.busy = false;
    }
  }

  /** The preview's object URL, revoked when it is replaced: a blob URL holds its bytes alive until
   *  it is, and this dialog can be opened many times on a board with a megabyte behind it. */
  render() {
    return html`<vitesse-dialog .open=${this.open} heading=${t('dashboards.editHeading')} @dialog-close=${this.close}>
      ${this.error ? html`<p class="error">${this.error}</p>` : nothing}
      <div class="field">
        <label for="title">${t('dashboards.titleLabel')}</label>
        <vitesse-text-input
          id="title"
          .value=${this.boardTitle}
          placeholder=${t('dashboards.titlePlaceholder')}
          @input=${(e: Event) => (this.boardTitle = (e.target as HTMLInputElement).value)}
        ></vitesse-text-input>
      </div>
      <div class="field">
        <label for="description">${t('dashboards.descriptionLabel')}</label>
        <vitesse-text-input
          id="description"
          multiline
          rows="3"
          .value=${this.description}
          placeholder=${t('dashboards.descriptionPlaceholder')}
          @input=${(e: Event) => (this.description = (e.target as HTMLTextAreaElement).value)}
        ></vitesse-text-input>
      </div>
      <vitesse-dashboard-background-field
        .background=${this.background}
        @background-change=${(e: CustomEvent<{ background?: DashboardBackground }>) => (this.background = e.detail.background)}
        @background-error=${(e: CustomEvent<{ message: string }>) => (this.error = e.detail.message)}
      ></vitesse-dashboard-background-field>
      <vitesse-button slot="footer" color="red" @click=${this.close}>${t('dashboards.cancel')}</vitesse-button>
      <vitesse-button slot="footer" color="blue" ?disabled=${this.busy || !this.boardTitle.trim()} @click=${() => void this.submit()}>
        ${this.busy ? t('dashboards.saving') : t('dashboards.editConfirm')}
      </vitesse-button>
    </vitesse-dialog>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-edit-dashboard-dialog': VitesseEditDashboardDialog;
  }
}
